import { defineStore } from "pinia"
import errorHandler from "@/services/ErrorService/errorHandler"
import { useCurrentAccountStore } from "./currentAccount"
import { useSnackbarStore } from "./snackbar"
import { PaymentMutator } from "@/mutators/payment/PaymentMutator"
import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
import { formatDateTime } from "@/helpers/dateHelper"
import { RouteNames } from "@/types/router"
import { ChangePaymentChoice } from "@/constants/finance"
import { EstimationChoice } from "@/types/ReviewPayment"
import router from "@/plugins/router"
import dayjs, { Dayjs } from "dayjs"

export interface IReviewPaymentStore {
  reason: string | null
  selectedFuels: IMeterpoint[]
  selectedEstimates: EstimationChoice[]
  usageEstimatedMeterpoints: IMeterpoint[]
  useUsageEstimatedMeterpoints: boolean
  amount: number | null
  dayOfMonth: string | null
  fromDate: Date | null
  isSeasonal: boolean
  note: string | null
  showBasicEstimationFor: (meterpoint: IMeterpoint) => boolean
  meterpointsWithNewUsageEstimations: IMeterpoint[]
  dateOfFirstPayment: Dayjs | null
  suggestedAmount: number | null
  amountIsLowerThanSuggested: boolean
}

export const defaultState = {
  reason: null,
  selectedFuels: [],
  selectedEstimates: [],
  usageEstimatedMeterpoints: [],
  useUsageEstimatedMeterpoints: false,
  amount: null,
  dayOfMonth: null,
  fromDate: null,
  isSeasonal: false,
  note: null,
}

export const useReviewPaymentStore = defineStore("reviewPayment", {
  state: () => {
    return {
      ...defaultState,
    } as unknown as IReviewPaymentStore
  },
  actions: {
    submit() {
      try {
        if (this.amount && this.dateOfFirstPayment) {
          PaymentMutator.updateSchedule()
            .mutateAsync({
              equalPaymentAmount: this.amount,
              seasonal: this.isSeasonal,
              firstPaymentDate: this.dateOfFirstPayment,
              fromDt: dayjs(this.fromDate),
            })
            .then(() => {
              useCurrentAccountStore()
                .addNote()
                .mutate({
                  subject: "General Note",
                  summary: "Payment Schedule Updated",
                  content: `Updated the customer payment schedule on ${formatDateTime(
                    dayjs(),
                  )}`,
                })
            })
            .then(() => {
              if (this.amountIsLowerThanSuggested) {
                useCurrentAccountStore()
                  .addNote()
                  .mutate({
                    subject: "General Note",
                    summary: "Payment Schedule Updated",
                    content: `Updated the customer payment schedule on ${formatDateTime(
                      dayjs(),
                    )} with an amount lower than the suggested amount: '${
                      this.note
                    }'`,
                  })
              }

              this.reset()
              useSnackbarStore().addSuccess(
                "Payment review successfully submitted!",
              )
              useCurrentAccountStore().paymentSchedules.refetch()
              void router.push({ name: RouteNames.CustomerFinancials })
            })
            .catch((error: Error) => {
              useSnackbarStore().addError(
                errorHandler.userMessage(
                  error,
                  "Failed to update payment schedule",
                ),
              )
            })
        }
      } catch (error) {
        useSnackbarStore().addError("Payment review submission failed")
        throw error
      }
    },
    reset() {
      Object.assign(this, defaultState)
    },
  },
  getters: {
    showBasicEstimationFor(state) {
      return (meterpoint: IMeterpoint) =>
        state.reason === ChangePaymentChoice.Circumstance ||
        !meterpoint.totalUsage
    },
    meterpointsWithNewUsageEstimations(state) {
      return useCurrentAccountStore().meterpoints.data.map(
        (meterpoint) =>
          state.usageEstimatedMeterpoints.find(
            (item) => item?.id === meterpoint.id,
          ) ?? meterpoint,
      )
    },
    dateOfFirstPayment(state) {
      const from = state.fromDate ?? new Date()
      const firstDate = new Date(
        from.getFullYear(),
        from.getMonth(),
        Number(state.dayOfMonth),
      )
      if (from.getDate() > Number(state.dayOfMonth)) {
        firstDate.setMonth(firstDate.getMonth() + 1)
      }
      return dayjs(firstDate)
    },
    suggestedAmount(state) {
      return useCurrentAccountStore().suggestedPayment(
        true,
        true,
        state.useUsageEstimatedMeterpoints
          ? state.meterpointsWithNewUsageEstimations
          : undefined,
      ).data
    },
    amountIsLowerThanSuggested(state) {
      if (this.suggestedAmount) {
        return state.amount && state.amount < this.suggestedAmount
      }
    },
  },
})
